import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  Row,
  SplitButton,
} from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import VoucherBasicInformation from "./VoucherBasicInformation";
import { CostCenterAPI, VoucherAPI, VouchersAPI } from "../../../../../../api";
import { toast } from "react-toastify";
import { customSettings } from "utils/customConfig";
import { useSettingValue } from "hooks/useSettingValue";
import { useSettingContext } from "context/SettingContext";
import { useFetchFinanceBook } from "hooks/useFetchFinanceBook";
import { FaCog, FaPrint } from "react-icons/fa";
import { CONSTANT_ROUTES } from "routes";
import PageTitle from "utils/PageTitle";

const schema = yup.object({
  type: yup.string().required("Type is required"),
  number: yup.string().required("Number is required"),
  fiananceBooks: yup
    .array()
    .of(
      yup.object({
        label: yup.string(),
        value: yup.string(),
      })
    )
    .required("Finance Book is required"),
  date: yup.string().required("Date is required"),
  postingDate: yup.string().required("Posting Date is required"),
  entries: yup
    .array()
    .of(
      yup.object({
        accountId: yup.string().required("Account is required"),
        subLedgerName: yup.string(),
        description: yup.string().required("Description is required"),
        debit: yup
          .number()
          .nullable()
          .transform((val) => (isNaN(val) ? 0 : val))
          .test(
            "not-both",
            "Enter either Debit or Credit, not both",
            function (value) {
              const { credit } = this.parent;
              if ((value || 0) > 0 && (credit || 0) > 0) {
                return false;
              }
              return true;
            }
          )
          .test(
            "not-none",
            "Either Debit or Credit must be entered",
            function (value) {
              const { credit } = this.parent;
              if ((value || 0) === 0 && (credit || 0) === 0) {
                return false;
              }
              return true;
            }
          ),
        credit: yup
          .number()
          .nullable()
          .transform((val) => (isNaN(val) ? 0 : val)),
        sortOrder: yup.string(),
        status: yup.boolean(),
      })
    )
    .test(
      "balanced-debit-credit",
      "Total Debit and Credit must be equal",
      function (entries) {
        if (!entries) return true;
        const totalDebit = entries.reduce(
          (sum, item) => sum + (parseFloat(item.debit) || 0),
          0
        );
        const totalCredit = entries.reduce(
          (sum, item) => sum + (parseFloat(item.credit) || 0),
          0
        );
        if (totalDebit !== totalCredit) {
          return this.createError({
            path: "entries",
            message: `Debits (${totalDebit}) and Credits (${totalCredit}) must match`,
          });
        }
        return true;
      }
    )
    .required("Details are required"),
  status: yup.boolean(),
});

// const schema = yup.object({
//   type: yup.string().required("Type is required"),
//   number: yup.string().required("Number is required"),
//   costCenterId: yup.string(),
//   fiananceBooks: yup
//     .array()
//     .of(
//       yup.object().shape({
//         label: yup.string(),
//         value: yup.number(),
//       })
//     )
//     .required("Finance Book is required"),
//   date: yup.string().required("Date is required"),
//   postingDate: yup.string().required("Posting Date is required"),
//   // entries: yup
//   //   .array()
//   //   .of(
//   //     yup.object().shape({
//   //       accountId: yup.string().required("Account is required"),
//   //       subLedgerName: yup.string().required("Sub Ledger Name is required"),
//   //       description: yup.string().required("Description is required"),
//   //       debit: yup
//   //         .number()
//   //         .nullable()
//   //         .transform((value) => (isNaN(value) ? 0 : value))
//   //         .test(
//   //           "only-one-field",
//   //           "Enter either Debit or Credit, not both",
//   //           function (debit) {
//   //             const { credit } = this.parent;
//   //             return !(debit > 0 && credit > 0); // Ensure only one is filled
//   //           }
//   //         ),
//   //       credit: yup
//   //         .number()
//   //         .nullable()
//   //         .transform((value) => (isNaN(value) ? 0 : value))
//   //         .test(
//   //           "only-one-field",
//   //           "Enter either Debit or Credit, not both",
//   //           function (credit) {
//   //             const { debit } = this.parent;
//   //             return !(debit > 0 && credit > 0); // Ensure only one is filled
//   //           }
//   //         ),
//   //       sortOrder: yup.string(),
//   //       status: yup.boolean(),
//   //     })
//   //   )
//   //   .test(
//   //     "balanced-debit-credit",
//   //     "Total debit and total credit must be equal",
//   //     function (entries) {
//   //       if (!entries || entries.length === 0) return true;

//   //       const totalDebit = entries.reduce(
//   //         (sum, item) => sum + (parseFloat(item.debit) || 0),
//   //         0
//   //       );
//   //       const totalCredit = entries.reduce(
//   //         (sum, item) => sum + (parseFloat(item.credit) || 0),
//   //         0
//   //       );

//   //       return totalDebit === totalCredit;
//   //     }
//   //   )
//   //   .required("Details is required"),
//   status: yup.boolean(),
// });

const AddVoucher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const hasDropdownItems = false;

  const submittedValues = {};
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { entries: [{}] },
  });
  const { handleSubmit, reset, setValue } = methods;

  const settingsData = useSettingContext();
  const costCenter = useMemo(
    () => settingsData?.find((d) => d.name === "DEFAULT_COST_CENTER"),
    [settingsData]
  );
  const costCenterId = Number(costCenter?.value) || 1; // Ensure it's a number

  const { data: financeBooksResponse, isLoading: financeBookLoading } =
    useFetchFinanceBook(costCenterId);

  // console.log(costCenterId);
  const onSubmit = async (data) => {
    const totalDebit = data.entries.reduce(
      (sum, item) => sum + (parseFloat(item.debit) || 0),
      0
    );
    const totalCredit = data.entries.reduce(
      (sum, item) => sum + (parseFloat(item.credit) || 0),
      0
    );

    if (totalDebit !== totalCredit) {
      toast.error("Total Debit and Total Credit must be equal", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
      return;
    }

    console.log(data);

    let myData = {
      ...data,
      // totalDebit,
      // totalCredit,
      costCenterId: costCenterId,
      postingStatus: "",
      fiananceBooks: data?.fiananceBooks?.map((book) => book.label).join("|"), // Convert the array to a string
      id: location?.state?.id ?? 0, // Use nullish coalescing to handle undefined or null
    };

    console.log(myData);

    try {
      if (location?.state) {
        setLoading(true);
        await VouchersAPI.post(myData);
        setLoading(false);
        toast.success("Voucher Updated Successfully", {
          position: customSettings.toastPosition,
          autoClose: customSettings.toastClose,
        });
      } else {
        setLoading(true);
        await VouchersAPI.post(myData);
        setLoading(false);
        toast.success("Voucher Created Successfully", {
          position: customSettings.toastPosition,
          autoClose: customSettings.toastClose,
        });
      }

      // ------- Get all object keys form data and set empty values to reset ------------
      const keys = Object.keys(data);
      for (const key of keys) {
        submittedValues[key] = "";
      }
      reset({ ...submittedValues });
      navigate(-1);
    } catch (error) {
      toast.error("Something Went Wrong", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
      console.log(error);
    }
  };

  const formatDate = (dateStr) => {
    return dateStr ? dateStr.split("T")[0] : "";
  };

  useEffect(() => {
    if (location.state) {
      const { id } = location.state;
      const fetchData = async () => {
        try {
          setLoading(true);
          const response = await VoucherAPI.get({ params: { voucherId: id } });
          setData(response?.data?.data);
          const data = response?.data?.data;

          // console.log(data);

          const availableFinanceBooks =
            financeBooksResponse?.data?.data?.map((d) => ({
              label: d.name,
              value: d.id,
            })) || [];

          const {
            fiananceBooks,
            entries,
            date,
            postingDate,
            status,
            type,
            number,
            financeBooksArray, // ["FB-New", "FB-2"]
            // ... other fields
          } = data;

          // console.log("financeBooksArray", financeBooksArray);

          // Map the received finance books to options
          const selectedFinanceBooks =
            financeBooksArray?.map((bookName) => {
              const match = availableFinanceBooks.find(
                (b) => b.label === bookName
              );
              // console.log("match", match);
              return match || { label: bookName, value: bookName };
            }) || [];

          setValue("fiananceBooks", selectedFinanceBooks);
          setValue("entries", entries);
          setValue("type", type);
          setValue("number", number);
          setValue("date", formatDate(date));
          setValue("postingDate", formatDate(postingDate));
          setValue("status", status);

          // ... set other fields
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [location.state, setValue]);

  // useEffect(() => {
  //   if (location.state) {
  //     const { id } = location.state;
  //     const fetchData = async () => {
  //       try {
  //         setLoading(true);
  //         const response = await VoucherAPI.get({ params: { voucherId: id } });
  //         const data = response?.data?.data;
  //         const {
  //           fiananceBooks, // This is the "FB-New|FB-2" string (you might not need this)
  //           financeBooksArray, // This is ["FB-New", "FB-2"]
  //           entries,
  //           date,
  //           postingDate,
  //           status,
  //           type,
  //           number,
  //           ...rest
  //         } = data;

  //         // Set other fields
  //         setValue("entries", entries);
  //         setValue("type", type);
  //         setValue("number", number);
  //         setValue("date", formatDate(date));
  //         setValue("postingDate", formatDate(postingDate));
  //         setValue("status", status);

  //         // Transform financeBooksArray to the format your MultiSelect expects
  //         if (financeBooksArray && financeBooksArray.length > 0) {
  //           const financeBookOptions = financeBooksArray.map((book) => ({
  //             label: book,
  //             value: book, // Or you might need to find the corresponding ID from fiananceBooks data
  //           }));

  //           setValue("fiananceBooks", financeBookOptions);
  //         }
  //       } catch (error) {
  //         console.error("Error fetching data:", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     };
  //     fetchData();
  //   }
  // }, [location.state, setValue]);

  return (
    <>
      {/* <div className="py-2">
        <BreadCrumb />
      </div> */}

      <PageTitle name="Accounts | Vouchers" />

      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <Card.Header className="border-bottom">
              <Row className="flex-between-center ">
                <Col md>
                  <h5 className="fs-9 text-nowrap mb-2 mb-md-0">
                    {" "}
                    Accounts | Vouchers
                  </h5>
                </Col>
                <Col xs="auto">
                  {/* <DropdownButton
                    as={ButtonGroup}
                    // size="sm"
                    align="end"
                    title="Print"
                    className="me-2"
                  >
                    <Dropdown.Item
                      eventKey="1"
                      onClick={() =>
                        navigate(CONSTANT_ROUTES.VOUCHER_PRINT, {
                          state: data,
                        })
                      }
                    >
                      <FaPrint size={20} />
                      <span className="px-2">Print</span>
                    </Dropdown.Item>
                  </DropdownButton> */}
                  {hasDropdownItems ? (
                    <SplitButton
                      as={ButtonGroup}
                      title={
                        <div className="d-flex align-items-center">
                          <FaPrint size={20} />
                          <span style={{ marginLeft: "10px" }}>Print</span>
                        </div>
                      }
                      align="end"
                      className="me-4 custom-split-button"
                      onClick={() =>
                        navigate(CONSTANT_ROUTES.VOUCHER_PRINT, { state: data })
                      }
                      disabled={!data}
                    >
                      <Dropdown.Item eventKey="1">Option 1</Dropdown.Item>
                      <Dropdown.Item eventKey="2">Option 2</Dropdown.Item>
                    </SplitButton>
                  ) : (
                    <Button
                      className="me-3"
                      onClick={() =>
                        navigate(CONSTANT_ROUTES.VOUCHER_PRINT, { state: data })
                      }
                      disabled={!data}
                    >
                      <div className="d-flex align-items-center">
                        <FaPrint size={20} />
                        <span style={{ marginLeft: "10px" }}>Print</span>
                      </div>
                    </Button>
                  )}
                  {/* <SplitButton
                    as={ButtonGroup}
                    title={
                      <div className="d-flex align-items-center gap-2">
                        <FaPrint size={20} />
                        <span>Print</span>
                      </div>
                    }
                    align="end"
                    className="me-4"
                    onClick={() =>
                      navigate(CONSTANT_ROUTES.VOUCHER_PRINT, {
                        state: data,
                      })
                    }
                    disabled={!data}
                  >
                    <Dropdown.Item eventKey="1">Action</Dropdown.Item>
                    <Dropdown.Item eventKey="2">Another action</Dropdown.Item>
                    <Dropdown.Item eventKey="3">
                      Something else here
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="4">Separated link</Dropdown.Item>
                  </SplitButton> */}
                  <Button
                    variant="link"
                    className="text-secondary fw-medium p-0 me-3"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    Ok
                  </Button>
                </Col>
              </Row>
            </Card.Header>

            <VoucherBasicInformation apiLoading={loading} />
          </Card>
        </Form>
      </FormProvider>
    </>
  );
};

export default AddVoucher;
